import { FC, useState } from "react";
import CustomPopup from "../../../../custom-components/CustomPopup";
import { useTranslation } from "react-i18next";
import { useAppDispatch } from "../../../../custom-hooks/useAppDispatch";
import useCustomFormik from "../../../../custom-hooks/useCustomFormik";
import { UserServiceImpl } from "../../../../services/UserServiceImpl";
import {
    userProfileFields,
    userProfileSchemaFactory,
} from "../../../../validation-schemas/user-schemas/UserProfileSchema";
import CustomInput from "../../../../custom-components/form-inputs/CustomInput";
import MapIconSVG from "../../../resources/svg/MapIconSVG";
import LocationIconSVG from "../../../resources/svg/LocationIconSVG";
import LocationPointIconSVG from "../../../resources/svg/LocationPointIcon.SVG";
import CustomCheckbox from "../../../../custom-components/form-inputs/CustomCheckbox";
import CheckMarkIconSVG from "../../../resources/svg/CheckMarkIconSVG";
import { useSelector } from "react-redux";
import { selectUser, setUserProfileImage } from "../../../../slices/user-slice";
import DeleteAccount from "./DeleteAccount";
import AddImageButton from "../../../resources/components/AddImageButton";
import ProfileImage from "./ProfileImage";
import { FileServiceImpl } from "../../../../services/FileServiceImpl";
import { FileOwner } from "../../../../domain/enums/FileOwnerEnum";
import PhoneInfo from "../../../resources/components/phone-info/PhoneInfo";
import { StatusCodes } from "http-status-codes";
import { useErrorHandler } from "../../../../custom-hooks/base-hooks/useErrorHandler";
import phoneServiceFactory from "../../../../services/PhoneServiceImpl";
import CustomError from "../../../../custom-components/form-inputs/CustomError";
import useScreenSize from "../../../../custom-hooks/base-hooks/useScreenSize";
import CustomTextArea from "../../../../custom-components/form-inputs/CustomTextArea";
import EditSuccess from "./EditSuccess";
import useAutocompletedAddress from "../../../../custom-hooks/domain/useAutocompletedAddress";
import AutocompleteTooltip from "../../../resources/components/AutocompleteTooltip";

interface EditProfileProps {
    isVisible: boolean;
    setIsVisible: (value: boolean) => void;
}

const phoneService = phoneServiceFactory();
const EditProfile: FC<EditProfileProps> = (props) => {
    const [isSuccessModalVisible, setIsSuccessModalVisible] = useState(false);
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const { upTo1024, moreOrEqual1024 } = useScreenSize();
    const currentUser = useSelector(selectUser());
    const [profileImage, setProfileImage] = useState<File>(null);
    const {
        isError: duplicatedPhones,
        resetErrors: resetConflictErrors,
        errorKey: duplicatedPhonesKey,
    } = useErrorHandler(StatusCodes.CONFLICT, "errors.response.custom.duplicatedPhones");
    const { form, setErrorKey, errorKey } = useCustomFormik({
        initial: currentUser.id ? currentUser : null,
        validateOnMount: true,
        fields: userProfileFields,
        schema: userProfileSchemaFactory(t),
        onSubmit: async (values) => {
            resetConflictErrors();
            if (values.phones.every((item) => !item.phone) && !values.telegram) {
                setErrorKey("errors.contactsInfoIncomplete");
                return;
            }
            if (phoneService.checkDuplicates(values.phones)) {
                setErrorKey("errors.response.custom.duplicatedPhones");
                return;
            }
            if (duplicatedPhones()) {
                return;
            }
            if (profileImage) {
                const file = await dispatch(
                    FileServiceImpl.uploadFileOrReplaceThunk({
                        data: profileImage,
                        ownerType: FileOwner.USER,
                    })
                );
                if (file.meta.requestStatus !== "fulfilled") {
                    setErrorKey("errors.common");
                    return;
                } else {
                    dispatch(setUserProfileImage(file.payload));
                }
            }
            const user = await dispatch(UserServiceImpl.updateUserThunk(values));
            if (user.meta.requestStatus !== "fulfilled") {
                setErrorKey("errors.common");
                return;
            }
            setIsSuccessModalVisible(true);
        },
    });

    const autocompleteHandlers = useAutocompletedAddress(form);

    const allowGeoCheckboxHandler = () => {
        const value = !form.values.allowGeo;
        form.setFieldValue("allowGeo", value);
        if (value) {
            navigator.geolocation.getCurrentPosition(() => {});
        }
    };
    const closeHandler = () => {
        form.resetForm();
        form.validateForm();
    };
    const confirmHandler = () => {
        if (!form.isSubmitting) {
            form.handleSubmit();
        }
    };
    const uploadFileHandler = (file: File) => {
        setProfileImage(file);
    };

    const confirmSuccessModalHandler = () => {
        props.setIsVisible(false);
        setIsSuccessModalVisible(false);
    };

    return (
        <>
            <EditSuccess isVisible={isSuccessModalVisible} confirmHandler={confirmSuccessModalHandler} />
            <CustomPopup
                isVisible={props.isVisible}
                setIsVisible={props.setIsVisible}
                wrapClassName="dmin2:min-w-[1062px] di6:min-w-[800px] ti6:min-w-[650px] ti7:min-w-[362px]    h-full"
                title={t("userProfile.editProfile.editProfileTitle")}
                titleClassName="my-8 dmax2:my-6"
                confirmTitle={t("common.saveChanges")}
                confirmHandler={confirmHandler}
                confirmButtonClassName="w-[193px] dmax2:w-[133px] h-[50px] dmax2:h-[40px] mt-8 dmax2:mt-6"
                closeHandler={closeHandler}
                skipScrollUnlock={!form.isValid}
                nested
                errorElement={
                    errorKey && (
                        <div className="error mt-4 dmax2:mt-3 text-center">{t(duplicatedPhonesKey || errorKey)}</div>
                    )
                }
            >
                <div className="scrollbar-popup max-h-[50vh] min-w-[330px] tmax5:overflow-x-hidden">
                    <div className="flex justify-center dmax5:flex-col">
                        <AddImageButton
                            ImagePreview={ProfileImage}
                            Button={
                                <p className="H16B dmax5:H14B text-textWhite mt-3">
                                    {t("userProfile.editProfile.changePhoto")}
                                </p>
                            }
                            onFileAdd={uploadFileHandler}
                            onFileAddPopupContent={
                                <p className="H32B mmax2:H30B text-textFaded">
                                    {t("userProfile.editProfile.changePhotoHint")}
                                </p>
                            }
                        />
                        <div className="w-[444px] ti6:w-[507px] tmax4:w-[318px]">
                            <div className="mb-4 di6:mb-2 dmax5:mb-2 pt-[1px]">
                                <div className="flex items-center justify-between">
                                    <p className="H20B dmax2:H16B text-textWhite">
                                        {t("userProfile.fields.firstNameField")}
                                    </p>
                                    <CustomInput
                                        form={form}
                                        name="firstName"
                                        fields={userProfileFields}
                                        className="w-[330px] tmax4:w-[227px] h-[52px] dmax2:h-[45px]"
                                    />
                                </div>
                                <CustomError form={form} name="firstName" className="mt-2 dmax2:mt-1 text-end" />
                            </div>
                            <div className="mb-4 dmax2:mb-2">
                                <div className="flex items-center justify-between">
                                    <p className="H20B dmax2:H16B text-textWhite">
                                        {t("userProfile.fields.lastNameField")}
                                    </p>
                                    <CustomInput
                                        form={form}
                                        name="lastName"
                                        fields={userProfileFields}
                                        className="w-[330px] tmax4:w-[227px] h-[52px] dmax2:h-[45px]"
                                    />
                                </div>
                                <CustomError form={form} name="lastName" className="mt-2 dmax2:mt-1 text-end" />
                            </div>
                            <div className="flex">
                                <div className="relative mr-4 dmax2:mr-2 pl-[1px]">
                                    <MapIconSVG className="absolute left-6 top-[14px] dmax2:top-[10px] text-textFaded" />
                                    <CustomInput
                                        form={form}
                                        name="address"
                                        nestedKey="country"
                                        fields={userProfileFields.address.schema}
                                        className="w-full h-[52px] dmax2:h-[45px] pl-[60px]"
                                        onChange={autocompleteHandlers.autocompleteAddress("country")}
                                    />
                                    <CustomError form={form} name="address" nestedKey="country" className="mt-2" />
                                    <AutocompleteTooltip
                                        data={autocompleteHandlers.countries}
                                        resetCallback={autocompleteHandlers.resetCountries}
                                        selectCallback={autocompleteHandlers.selectCountry}
                                        className="px-[20px]"
                                    />
                                </div>
                                <div className="relative">
                                    <LocationPointIconSVG className="absolute left-6 top-[14px] dmax2:top-[10px] text-textFaded" />
                                    <CustomInput
                                        form={form}
                                        name="address"
                                        nestedKey="city"
                                        fields={userProfileFields.address.schema}
                                        className="w-full h-[52px] dmax2:h-[45px] pl-[60px]"
                                        onChange={autocompleteHandlers.autocompleteAddress("city")}
                                    />
                                    <CustomError
                                        form={form}
                                        name="address"
                                        nestedKey="city"
                                        className="mt-2 dmax2:mt-1"
                                    />
                                    <AutocompleteTooltip
                                        data={autocompleteHandlers.cities}
                                        resetCallback={autocompleteHandlers.resetCities}
                                        selectCallback={autocompleteHandlers.selectCity}
                                        className="px-[20px]"
                                    />
                                </div>
                            </div>
                            <div className="relative mt-4 dmax2:mt-2">
                                <div className="flex items-center pl-[1px]">
                                    <LocationIconSVG className="absolute w-6 h-6 left-6 top-[14px] dmax2:top-[10px] text-textFaded" />
                                    <CustomInput
                                        form={form}
                                        name="address"
                                        nestedKey="address"
                                        fields={userProfileFields.address.schema}
                                        className="w-full h-[52px] dmax2:h-[45px] pl-[60px]"
                                        onChange={autocompleteHandlers.autocompleteAddress("address")}
                                    />
                                </div>
                                <CustomError
                                    form={form}
                                    name="address"
                                    nestedKey="address"
                                    className="mt-2 dmax2:mt-1 text-end"
                                />
                                <AutocompleteTooltip
                                    data={autocompleteHandlers.addresses}
                                    resetCallback={autocompleteHandlers.resetAddresses}
                                    selectCallback={autocompleteHandlers.selectAddress}
                                    className="px-[20px]"
                                />
                                <p className="H16 tmax4:H12 text-textFaded mt-2">
                                    {t("userProfile.editProfile.addressRule")}
                                </p>
                                <DeleteAccount />
                            </div>
                        </div>
                    </div>
                    <CustomCheckbox
                        labelKey="userProfile.fields.allowGeoField"
                        checked={form.values.allowGeo}
                        onClick={allowGeoCheckboxHandler}
                        labelClassName="H16 tmax4:H14 text-textWhite max-w-[496px] dmin2:max-w-[532px] ti6:max-w-[414px] ti7:max-w-[412px] tmax4:max-w-[280px]"
                        checkboxClassName="flex justify-center items-center w-6 h-6 border-2 border-textFaded rounded-sm cursor-pointer transition duration-200 mr-4 tmax4:mr-3"
                        checkboxCheckedClassName="!border-0 bg-textWarning"
                        wrapClassName="flex items-center tmax4:items-start mt-9 dmax5:mt-6 dmin2:w-[838px]"
                        additionalLayoutElement={<CheckMarkIconSVG />}
                    />
                    <div className="relative mt-9 dmax2:mt-6">
                        <p className="H20B dmax2:H16B text-textWhite mb-5 dmax2:mb-4 ">{t("userProfile.aboutMe")}</p>
                        <CustomTextArea
                            form={form}
                            name="about"
                            fields={userProfileFields}
                            charactersRestrictionKey="common.characters"
                        />
                        <CustomError form={form} name="about" className="mt-2 dmax2:mt-1" />
                    </div>
                    <div className="mt-9 dmax2:mt-6">
                        <p className="H20B dmax2:H16B text-textWhite mb-5 dmax2:mb-4">{t("common.contacts")}</p>
                        <p className="H16 tmax4:H14 text-textFaded w-[650px] ti6:w-[507px] ti7:w-[420px] tmax4:w-[320px]">
                            {t("userProfile.editProfile.contactsRule")}
                        </p>
                        <div className="flex justify-between">
                            <div className="flex flex-col w-[838px] di6:w-[674px] td5:w-[506px] ti7:w-[444px] tmax4:w-[318px]">
                                <PhoneInfo form={form} fields={userProfileFields} />
                                <div className="flex items-center tmax3:justify-between mt-4 dmax2:mt-3 pb-[1px]">
                                    <p className="flex items-center H16B dmax2:H14B text-textWhite tmax4:w-[75px] td4:min-w-[135px] dmin2:w-[200px] tmin3:mr-3">
                                        <img
                                            src="/images/telegram.png"
                                            alt="telegram"
                                            className="w-9 tmax4:w-8 h-9 tmax4:h-8 mr-3 dmax2:mr-2"
                                        />
                                        <span>{t("userProfile.fields.telegramField")}</span>
                                    </p>
                                    <CustomInput
                                        form={form}
                                        name="telegram"
                                        fields={userProfileFields}
                                        className="w-[330px] di6:w-[300px] ti7:w-[250px] tmax4:w-[204px] h-[52px] dmax2:h-[45px]"
                                    />
                                </div>
                                {moreOrEqual1024 && (
                                    <CustomError
                                        form={form}
                                        name="telegram"
                                        className="dmin2:pl-[212px] di6:pl-[148px] mt-2 dmax2:mt-1"
                                    />
                                )}
                                {upTo1024 && (
                                    <CustomError errorKey={form.errors.telegram} className="mt-2 dmax2:mt-1 text-end" />
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </CustomPopup>
        </>
    );
};

export default EditProfile;
